import React, { useState, useEffect } from 'react';
import './AssetDetail.css';
import NewsContextCard from './NewsContextCard';

const API = '';

const SECTOR_EMOJI = {
  Bancos: '🏦', Petróleo: '🛢', Mineração: '⛏', Varejo: '🛒',
  Consumo: '🥩', Industrial: '⚙', Energia: '⚡', Saúde: '💊',
  Imobiliário: '🏗', ETF: '📊', Tecnologia: '💻', Utilities: '🔌',
};

function fmtZ(v) {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}σ`;
}
function fmtPrice(v) {
  if (v == null) return '—';
  return `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v) {
  if (v == null) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function zToPos(z) {
  if (z == null) return 50;
  const clamped = Math.max(-3, Math.min(3, z));
  return ((clamped + 3) / 6) * 100;
}

function generateInsight(asset) {
  const { ticker, sector, zKairi, zSector, gap, diagnosis } = asset;
  const zA = fmtZ(zKairi);
  const zS = fmtZ(zSector);
  const g  = gap != null ? gap.toFixed(2) : '—';

  if (diagnosis === 'filtered') {
    return {
      variant: 'neutral',
      title: `${ticker} está abaixo da MM200.`,
      body: `O filtro de tendência bloqueou este ativo. A queda pode ser estrutural — o sistema evita entradas contra a tendência de longo prazo. Aguarde retorno acima da MM200 antes de considerar mean reversion.`,
      conclusion: null,
    };
  }
  if (diagnosis === 'systemic') {
    return {
      variant: 'systemic',
      title: `${ticker} está caindo junto com todo o setor de ${sector}.`,
      body: `Z-score do ativo (${zA}) é praticamente igual à média setorial (${zS}) — gap de apenas ${g}σ. A queda é sistêmica, provavelmente disparada por fator macro. Probabilidade de reversão à média é alta porque toda a indústria deve recuperar conjuntamente.`,
      conclusion: 'Cenário ideal para mean reversion.',
    };
  }
  if (diagnosis === 'idiosyncratic') {
    return {
      variant: 'warning',
      title: `${ticker} está caindo mais do que o setor de ${sector}.`,
      body: `Gap de ${g}σ entre o ativo (${zA}) e a média setorial (${zS}) indica queda parcialmente idiossincrática. Pode haver fator específico afetando este papel. Confirme se há notícias ou eventos corporativos antes de operar.`,
      conclusion: 'Investigue antes de executar.',
    };
  }
  if (diagnosis === 'danger') {
    return {
      variant: 'danger',
      title: `${ticker} está caindo SOZINHO — evento idiossincrático extremo.`,
      body: `Gap de ${g}σ entre o ativo (${zA}) e o setor (${zS}) é estatisticamente extremo. O setor de ${sector} está praticamente neutro enquanto este papel despenca. Isso NÃO é setup clássico de mean reversion — há maior probabilidade de continuação da queda. Exija confirmação fundamental antes de qualquer entrada.`,
      conclusion: null,
    };
  }
  if (diagnosis === 'watch') {
    return {
      variant: 'neutral',
      title: `${ticker} em zona de atenção — aguardando confirmação.`,
      body: `Z-score de ${zA} é extremo mas os dados setoriais são insuficientes para classificar a queda. Monitore por mais um dia antes de tomar decisão.`,
      conclusion: 'Sinal watch — não operar ainda.',
    };
  }
  return {
    variant: 'neutral',
    title: `${ticker} sem sinal ativo hoje.`,
    body: `Z-score de ${zA} não atingiu o limiar de entrada (−1,5σ). O sistema não detectou distorção estatística extrema neste ativo.`,
    conclusion: null,
  };
}

function ZScoreBar({ z, label, side }) {
  if (z == null) return null;
  const pos = zToPos(z);
  const neg = z < 0;
  const strong = z <= -1.5 || z >= 1.5;
  return (
    <div className={`ad-pin ad-pin-${side}`} style={{ left: `${pos}%` }}>
      <div className={`ad-pin-dot ad-pin-${side}-dot ${strong ? 'strong' : ''}`} />
      <div className={`ad-pin-label ad-pin-${side}-label`}>
        <span className="ad-pin-name">{label}</span>
        <span className={`ad-pin-z ${neg ? 'neg' : 'pos'}`}>{fmtZ(z)}</span>
      </div>
    </div>
  );
}

function SkeletonDetail() {
  return (
    <div className="ad-skeleton">
      {[1,2,3].map(i => (
        <div key={i} className="ad-sk-block" style={{ height: i === 2 ? 160 : 80 }} />
      ))}
    </div>
  );
}

export default function AssetDetail({ ticker, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [execOpen, setExecOpen] = useState(false);
  const [execQty, setExecQty]   = useState('');
  const [execPrice, setExecPrice] = useState('');

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    setData(null);
    fetch(`${API}/api/asset/${ticker}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(d => { setData(d); setExecPrice(String(d.price ?? '')); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) return <div className="ad-root"><SkeletonDetail /></div>;
  if (error)   return (
    <div className="ad-root ad-error">
      <div className="ad-error-icon">⚠</div>
      <div>Erro ao carregar dados de {ticker}</div>
      <div className="ad-error-sub">{error}</div>
      <button className="ad-back-btn" onClick={onBack}>← Voltar</button>
    </div>
  );
  if (!data) return null;

  const insight  = generateInsight(data);
  const showPlan = data.signal === 'buy';
  const diagLabel = {
    systemic:      'Sistêmica',
    idiosyncratic: 'Idiossincrática',
    danger:        'Extrema',
    filtered:      'Filtrado (MM200)',
    watch:         'Watch',
    normal:        'Normal',
  }[data.diagnosis] || data.diagnosis;

  const sectorEmoji = SECTOR_EMOJI[data.sector] || '📊';

  const TICKS = [-3, -2, -1, 0, 1, 2, 3];

  return (
    <div className="ad-root">

      {/* Back */}
      <button className="ad-back-btn" onClick={onBack}>← Scanner</button>

      {/* Verdict Banner */}
      <div className={`ad-verdict ad-v-${data.diagnosis}`}>
        <div className="ad-verdict-left">
          <div className="ad-verdict-ticker">{data.ticker}</div>
          <div className="ad-verdict-name">{data.name}</div>
          <div className="ad-verdict-meta">
            {sectorEmoji} {data.sector}
            {data.price != null && <span> · {fmtPrice(data.price)}</span>}
          </div>
        </div>
        <div className={`ad-verdict-badge ad-badge-${data.diagnosis}`}>
          {data.diagnosis === 'systemic'      && '✓ '}
          {data.diagnosis === 'idiosyncratic' && '⚠ '}
          {data.diagnosis === 'danger'        && '⛔ '}
          {data.diagnosis === 'filtered'      && '⏸ '}
          {data.diagnosis === 'watch'         && '👁 '}
          {diagLabel}
        </div>
      </div>

      {/* Z-Score Régua */}
      <div className="ad-card">
        <div className="ad-card-head">
          <div className="ad-card-title">Z-Kairi normalizado</div>
          <div className="ad-card-sub">Distância da MM25 em desvios-padrão de 252 dias</div>
        </div>

        <div className="ad-ruler-wrap">
          <div className="ad-ruler-ticks">
            {TICKS.map(t => (
              <span key={t} className="ad-tick" style={{ left: `${((t + 3) / 6) * 100}%` }}>
                {t > 0 ? '+' : ''}{t}σ
              </span>
            ))}
          </div>

          <div className="ad-ruler">
            {/* Asset pin (below) */}
            {data.zKairi != null && (
              <div className="ad-pin-asset" style={{ left: `${zToPos(data.zKairi)}%` }}>
                <div className={`ad-dot ad-dot-asset ${data.zKairi <= -1.5 ? 'extreme' : ''}`} />
                <div className="ad-pin-asset-label">
                  <span className="ad-pin-ticker">{data.ticker}</span>
                  <span className="ad-pin-val neg">{fmtZ(data.zKairi)}</span>
                </div>
              </div>
            )}

            {/* Sector pin (above) */}
            {data.zSector != null && (
              <div className="ad-pin-sector" style={{ left: `${zToPos(data.zSector)}%` }}>
                <div className="ad-pin-sector-label">
                  <span className="ad-pin-sector-name">Setor</span>
                  <span className={`ad-pin-val ${data.zSector < 0 ? 'neg' : 'pos'}`}>{fmtZ(data.zSector)}</span>
                </div>
                <div className="ad-dot ad-dot-sector" />
              </div>
            )}
          </div>

          <div className="ad-ruler-legend">
            <span><span className="ad-leg-dot asset" /> {data.ticker} (este ativo)</span>
            {data.zSector != null && <span><span className="ad-leg-dot sector" /> Média do setor</span>}
          </div>
        </div>

        {/* Gap warning */}
        {data.gap != null && data.gap > 1.5 && (
          <div className="ad-gap-warn">
            <span>⚠️ Gap de </span>
            <strong>{data.gap.toFixed(2)}σ</strong>
            <span> entre o ativo e seu setor — sinal de evento idiossincrático</span>
          </div>
        )}
      </div>

      <div className="ad-card ad-sector-beta">
        <div className="ad-card-head">
          <div className="ad-card-title">Setor e beta</div>
          <div className="ad-card-sub">Contexto setorial incorporado ao detalhe do ativo</div>
        </div>
        <div className="ad-sector-beta-grid">
          <div><span>Setor</span><strong>{data.sector || '—'}</strong></div>
          <div><span>β setor</span><strong>{data.betaSector == null ? '—' : Number(data.betaSector).toFixed(2)}</strong></div>
          <div><span>β IBOV</span><strong>{data.betaIbovespa == null ? '—' : Number(data.betaIbovespa).toFixed(2)}</strong></div>
        </div>
      </div>

      {/* Peers Table */}
      {data.peers?.length > 1 && (
        <div className="ad-card">
          <div className="ad-card-head">
            <div className="ad-card-title">{sectorEmoji} Pares do setor — {data.sector}</div>
            <div className="ad-card-sub">
              {data.diagnosis === 'systemic'
                ? 'Todos os pares estão na mesma região — queda sistêmica confirmada'
                : 'Compare o Z-score deste ativo com os pares do setor'}
            </div>
          </div>
          <table className="ad-peers-table">
            <thead>
              <tr>
                <th>Ativo</th>
                <th>Preço</th>
                <th>Z-Kairi</th>
                <th>MM200</th>
              </tr>
            </thead>
            <tbody>
              {data.peers.map(p => (
                <tr key={p.ticker} className={p.isCurrent ? 'ad-peer-current' : ''}>
                  <td>
                    <div className="ad-peer-ticker">{p.ticker}{p.isCurrent ? ' ←' : ''}</div>
                    <div className="ad-peer-name">{p.name}</div>
                  </td>
                  <td className="mono">{fmtPrice(p.price)}</td>
                  <td>
                    <div className="ad-peer-zbar-wrap">
                      <div className="ad-peer-zbar-track">
                        <div className="ad-peer-zbar-center" />
                        {p.zKairi != null && (
                          <div
                            className={`ad-peer-zbar-fill ${p.zKairi < 0 ? 'neg' : 'pos'}`}
                            style={{
                              width: `${Math.min(Math.abs(p.zKairi) / 3 * 50, 50)}%`,
                              [p.zKairi < 0 ? 'right' : 'left']: '50%',
                            }}
                          />
                        )}
                      </div>
                      <span className={`ad-peer-z mono ${p.zKairi != null && p.zKairi <= -1.5 ? 'neg-strong' : ''}`}>
                        {fmtZ(p.zKairi)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`ad-mm200-badge ${p.aboveMm200 ? 'above' : 'below'}`}>
                      {p.aboveMm200 ? '↑ acima' : '↓ abaixo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewsContextCard
        ticker={data.ticker}
        eventType={data.diagnosis === 'idiosyncratic' ? 'queda idiossincrática' : 'detalhe do ativo'}
      />

      {/* Insight Box */}
      <div className={`ad-insight ad-insight-${insight.variant}`}>
        <div className="ad-insight-label">📊 Análise v3</div>
        <div className="ad-insight-title">{insight.title}</div>
        <div className="ad-insight-body">{insight.body}</div>
        {insight.conclusion && (
          <div className={`ad-insight-conclusion ad-concl-${insight.variant}`}>
            {insight.conclusion}
          </div>
        )}
      </div>

      {/* Trade Plan — only on buy signal */}
      {showPlan && data.price != null && (
        <div className="ad-card">
          <div className="ad-card-head">
            <div className="ad-card-title">📋 Plano de trade</div>
            <div className="ad-card-sub">Baseado em 1% de risco do capital por operação</div>
          </div>
          <div className="ad-trade-grid">
            <div className="ad-trade-card entry">
              <div className="ad-trade-icon">🎯</div>
              <div className="ad-trade-lbl">Entrada</div>
              <div className="ad-trade-val">{fmtPrice(data.price)}</div>
              <div className="ad-trade-sub">Próxima abertura</div>
            </div>
            <div className="ad-trade-card exit">
              <div className="ad-trade-icon">🟢</div>
              <div className="ad-trade-lbl">Saída (alvo)</div>
              <div className="ad-trade-val">{fmtPrice(data.target)}</div>
              <div className="ad-trade-sub">
                {data.price && data.target
                  ? fmtPct(((data.target - data.price) / data.price) * 100)
                  : '—'} · 2–5 dias
              </div>
            </div>
            <div className="ad-trade-card stop">
              <div className="ad-trade-icon">🔴</div>
              <div className="ad-trade-lbl">Stop loss</div>
              <div className="ad-trade-val">{fmtPrice(data.stop)}</div>
              <div className="ad-trade-sub">−7% · 1% capital</div>
            </div>
          </div>
          <div className="ad-disclaimer">
            Resultados passados não garantem retornos futuros.
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="ad-actions">
        {showPlan ? (
          <>
            <button className="ad-btn-exec" onClick={() => setExecOpen(true)}>
              ✓ Marcar como executado
            </button>
            <button className="ad-btn-sec" onClick={onBack}>
              Ver outros ativos
            </button>
          </>
        ) : (
          <>
            <button className="ad-btn-sec" onClick={onBack}>
              📊 Ir para outro ativo
            </button>
          </>
        )}
      </div>

      {/* Exec Modal */}
      {execOpen && (
        <div className="ad-modal-overlay" onClick={() => setExecOpen(false)}>
          <div className="ad-modal" onClick={e => e.stopPropagation()}>
            <div className="ad-modal-title">Registrar operação — {data.ticker}</div>
            <div className="ad-modal-row">
              <label>Quantidade (cotas)</label>
              <input
                type="number"
                value={execQty}
                onChange={e => setExecQty(e.target.value)}
                placeholder="100"
                className="ad-modal-input"
              />
            </div>
            <div className="ad-modal-row">
              <label>Preço de execução (R$)</label>
              <input
                type="number"
                step="0.01"
                value={execPrice}
                onChange={e => setExecPrice(e.target.value)}
                className="ad-modal-input"
              />
            </div>
            <div className="ad-modal-actions">
              <button className="ad-btn-exec" onClick={() => setExecOpen(false)}>
                ✓ Registrar na carteira
              </button>
              <button className="ad-btn-sec" onClick={() => setExecOpen(false)}>
                Cancelar
              </button>
            </div>
            <div className="ad-disclaimer">
              Resultados passados não garantem retornos futuros. Operação registrada apenas como carteira virtual.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
